import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import { getRecordTypeRelKey } from '../../config/gobodoAccountRelationshipKeys';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import { shouldUseGobodo, USE_GOBODO_API } from '../../utils/gobodoUtils';
import { queryClient } from '../../queryClient';
import { fetchCreditBlockRecordTypeTargetCached } from '../queries/creditBlockRecordTypeTarget';

const CUSTOM_SUFFIX = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';
const CREDIT_BLOCK_RECORD_TYPE_NAME =
  process.env.REACT_APP_CREDIT_BLOCK_RECORD_TYPE_NAME || 'Credit Block';

export interface CreditBlockRecordTypeTarget {
  key: string;
  sfId: string;
  label: string;
}

interface CaseRelationshipLocator {
  caseId?: string | number | null;
  caseKey?: string | null;
}

function stripSuffix(value: string): string {
  return String(value).replace(/__stg3_c$|__stg_c$|__dev_c$|__c$/i, '');
}

function sanitizeRelationshipKeyPart(value: string): string {
  return value.replace(/[^A-Za-z0-9_]+/g, '_');
}

function buildStableCaseRelationshipKey(
  locator: CaseRelationshipLocator,
  kind: 'record_type'
): string {
  const base = String(locator.caseKey ?? locator.caseId ?? '').trim();
  if (!base) {
    throw new Error(`Cannot build ${kind} relationship key without a case id or key`);
  }
  return `case_${sanitizeRelationshipKeyPart(base)}_${kind}${CUSTOM_SUFFIX}`;
}

function buildCaseRelationshipPayload(
  locator: CaseRelationshipLocator,
  relationshipTypeKey: string,
  relationshipKey: string,
  toRef: string
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    key: relationshipKey,
    relationshiptypekey: relationshipTypeKey,
  };
  const caseKey = String(locator.caseKey ?? '').trim();
  const caseId = String(locator.caseId ?? '').trim();
  const toToken = String(toRef ?? '').trim();
  if (caseKey) {
    payload.fromKey = caseKey;
  } else if (caseId && /^[0-9]+$/.test(caseId)) {
    payload.fromId = Number(caseId);
  } else {
    throw new Error(`Cannot upsert relationship without a valid case key or numeric case id: ${caseId}`);
  }
  if (!toToken) {
    throw new Error(`Cannot upsert relationship ${relationshipTypeKey} without an end-object reference`);
  }
  if (/^[0-9]+$/.test(toToken)) {
    payload.toId = Number(toToken);
  } else {
    payload.toKey = toToken;
  }
  return payload;
}

async function upsertCaseRelationship(
  locator: CaseRelationshipLocator,
  relationshipTypeKey: string,
  relationshipKey: string,
  toKey: string
): Promise<void> {
  if (!USE_GOBODO_API || !shouldUseGobodo()) return;
  const client = getGobodoClient();
  const relationshipsApi = new GobodoApi.GraphRelationshipsApi(client);
  const payload = buildCaseRelationshipPayload(locator, relationshipTypeKey, relationshipKey, toKey);
  await retryWithBackoff(
    () => relationshipsApi.upsertRelationships([payload]),
    { maxRetries: 2, initialDelayMs: 1000 }
  );
}

function extractRecordTypeTarget(result: unknown): CreditBlockRecordTypeTarget | null {
  if (result == null || typeof result !== 'object') return null;
  const record = result as Record<string, unknown>;
  const data = (record.data as Record<string, unknown>) ?? record;
  const key = String(record.key ?? data.key ?? '').trim();
  if (!key) return null;
  const label = String(record.label ?? data.Name__c ?? data.name ?? CREDIT_BLOCK_RECORD_TYPE_NAME).trim();
  const sfCandidates = [
    data[`sf_id${CUSTOM_SUFFIX}`],
    record[`sf_id${CUSTOM_SUFFIX}`],
    data.sf_id,
    record.sf_id,
    data.SfId__c,
    record.SfId__c,
    data.Id__c,
    record.Id__c,
  ];
  const sfId =
    sfCandidates
      .map((candidate) => stripSuffix(String(candidate ?? '').trim()))
      .find((candidate) => /^012[A-Za-z0-9]{12}([A-Za-z0-9]{3})?$/.test(candidate)) ?? '';
  return { key, sfId, label };
}

export async function findCreditBlockRecordTypeTarget(): Promise<CreditBlockRecordTypeTarget | null> {
  if (!USE_GOBODO_API || !shouldUseGobodo()) return null;
  const result = await fetchCreditBlockRecordTypeTargetCached(queryClient);
  return extractRecordTypeTarget(
    result
      ? {
          key: result.objectKey,
          data: {
            [`sf_id${CUSTOM_SUFFIX}`]: result.sfId ?? '',
            Name__c: result.label,
          },
        }
      : null
  );
}

export async function ensureCreditBlockCaseRecordTypeRelationship(
  locator: CaseRelationshipLocator,
  target?: CreditBlockRecordTypeTarget | null
): Promise<CreditBlockRecordTypeTarget> {
  const resolvedTarget = target ?? (await findCreditBlockRecordTypeTarget());
  if (!resolvedTarget?.key) {
    throw new Error(`Could not resolve the ${CREDIT_BLOCK_RECORD_TYPE_NAME} record type in Gobodo`);
  }
  await upsertCaseRelationship(
    locator,
    getRecordTypeRelKey(),
    buildStableCaseRelationshipKey(locator, 'record_type'),
    resolvedTarget.key
  );
  return resolvedTarget;
}

export function applyCreditBlockRecordTypeFields(
  payload: Record<string, unknown>,
  target: CreditBlockRecordTypeTarget | null | undefined
): void {
  if (!target) return;
  if (target.key) {
    payload.record_type = target.key;
    const strippedKey = stripSuffix(target.key);
    payload.RecordType__c = strippedKey;
    payload.recordType = strippedKey;
  }
  if (target.sfId) {
    payload.RecordTypeId__c = target.sfId;
    payload.recordTypeId = target.sfId;
  }
}
